import { RunLogEntry } from '../types';
import { STANDARD_RACES, vdotFromPerformance } from './performance';

/** Maandag 00:00 (lokale tijd) van de week waarin `date` valt. */
export function startOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysSinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday);
  return d;
}

export interface WeekVolume {
  /** Maandag van de week */
  weekStart: Date;
  km: number;
  runs: number;
}

/** Kilometers per week over de laatste `weeks` weken, oudste eerst, inclusief de lopende week. */
export function weeklyVolume(logs: RunLogEntry[], weeks = 12, now = new Date()): WeekVolume[] {
  const thisWeek = startOfWeek(now);
  const result: WeekVolume[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const weekStart = new Date(thisWeek);
    weekStart.setDate(weekStart.getDate() - i * 7);
    result.push({ weekStart, km: 0, runs: 0 });
  }
  const first = result[0].weekStart.getTime();
  for (const run of logs) {
    const time = new Date(run.date).getTime();
    if (Number.isNaN(time) || time < first) continue;
    const runWeek = startOfWeek(new Date(time)).getTime();
    const bucket = result.find((w) => w.weekStart.getTime() === runWeek);
    if (bucket) {
      bucket.km += run.distanceKm;
      bucket.runs += 1;
    }
  }
  return result;
}

/**
 * Aantal weken op rij met minstens één run. De lopende week telt mee als er al gelopen is,
 * maar breekt de reeks niet als je daar nog niet aan toe bent gekomen.
 */
export function weekStreak(logs: RunLogEntry[], now = new Date()): number {
  const weeksWithRuns = new Set<number>();
  for (const run of logs) {
    const time = new Date(run.date).getTime();
    if (!Number.isNaN(time)) weeksWithRuns.add(startOfWeek(new Date(time)).getTime());
  }
  const cursor = startOfWeek(now);
  if (!weeksWithRuns.has(cursor.getTime())) cursor.setDate(cursor.getDate() - 7);
  let streak = 0;
  while (weeksWithRuns.has(cursor.getTime())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 7);
  }
  return streak;
}

// ---- Persoonlijke records ----

const RECORD_TOLERANCE = 0.02;

/** Welke standaardafstand (id uit STANDARD_RACES) hoort bij deze run? Binnen 2% van de afstand. */
export function recordCategory(distanceKm: number): string | null {
  for (const race of STANDARD_RACES) {
    if (Math.abs(distanceKm - race.km) / race.km <= RECORD_TOLERANCE) return race.id;
  }
  return null;
}

/** Tijd herleid naar de exacte standaardafstand, zodat 5,02 km eerlijk met 4,98 km vergeleken wordt. */
function normalizedSeconds(run: RunLogEntry, categoryId: string): number {
  const race = STANDARD_RACES.find((r) => r.id === categoryId)!;
  return (run.totalSeconds * race.km) / run.distanceKm;
}

/** Snelste run per standaardafstand: { categoryId: runId } */
export function personalRecords(logs: RunLogEntry[]): Record<string, string> {
  const best: Record<string, { id: string; seconds: number }> = {};
  for (const run of logs) {
    const cat = recordCategory(run.distanceKm);
    if (!cat || !(run.totalSeconds > 0)) continue;
    const seconds = normalizedSeconds(run, cat);
    if (!best[cat] || seconds < best[cat].seconds) best[cat] = { id: run.id, seconds };
  }
  return Object.fromEntries(Object.entries(best).map(([cat, v]) => [cat, v.id]));
}

/**
 * Is `run` een nieuw record ten opzichte van de bestaande runs? De allereerste run op een afstand telt
 * pas als record vanaf de tweede run op die afstand, anders is elke eerste run meteen "een record".
 */
export function newRecordCategory(run: RunLogEntry, existing: RunLogEntry[]): string | null {
  const cat = recordCategory(run.distanceKm);
  if (!cat) return null;
  const previous = existing.filter((r) => r.id !== run.id && recordCategory(r.distanceKm) === cat);
  if (previous.length === 0) return null;
  const fastestBefore = Math.min(...previous.map((r) => normalizedSeconds(r, cat)));
  return normalizedSeconds(run, cat) < fastestBefore ? cat : null;
}

// ---- Beste prestatie en trends ----

/** De run met de hoogste vormindex (VDOT); runs korter dan 3 minuten of langer dan 8 uur tellen niet. */
export function bestVdotRun(logs: RunLogEntry[]): { run: RunLogEntry; vdot: number } | null {
  let best: { run: RunLogEntry; vdot: number } | null = null;
  for (const run of logs) {
    const vdot = vdotFromPerformance(run.distanceKm, run.totalSeconds);
    if (Number.isFinite(vdot) && (!best || vdot > best.vdot)) best = { run, vdot };
  }
  return best;
}

/**
 * Verschil in gemiddeld tempo (sec/km) tussen de laatste 3 en de 3 daarvoor.
 * Negatief = sneller geworden. null bij minder dan 6 runs.
 */
export function paceTrend(logs: RunLogEntry[]): number | null {
  const sorted = [...logs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  if (sorted.length < 6) return null;
  const avg = (runs: RunLogEntry[]) => runs.reduce((s, r) => s + r.paceSecondsPerKm, 0) / runs.length;
  return avg(sorted.slice(0, 3)) - avg(sorted.slice(3, 6));
}

/** Gemiddeld tempo per gevoelsniveau (1-5); alleen niveaus met minstens één run. */
export function paceByFeeling(logs: RunLogEntry[]): { feeling: 1 | 2 | 3 | 4 | 5; pace: number; runs: number }[] {
  const out: { feeling: 1 | 2 | 3 | 4 | 5; pace: number; runs: number }[] = [];
  for (const feeling of [1, 2, 3, 4, 5] as const) {
    const runs = logs.filter((r) => r.feeling === feeling);
    if (runs.length > 0) {
      out.push({ feeling, pace: runs.reduce((s, r) => s + r.paceSecondsPerKm, 0) / runs.length, runs: runs.length });
    }
  }
  return out;
}
